import { ViewIds } from '@ardoq/api-types';
import { IconName } from '@ardoq/icons';
import { modal } from '@ardoq/modal';
import { dispatchAction } from '@ardoq/rxbeach';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { getFieldDropdownOptions } from '@ardoq/settings-bar';
import {
  SettingsType,
  type DropdownConfig,
  type ToggleConfig,
  updateViewSettings,
} from '@ardoq/view-settings';
import { DropdownOptionType, Notifier } from '@ardoq/dropdown-menu';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  BubbleChartGridMode,
  BubbleChartQuadrantSettings,
  BubbleChartViewSettings,
  BUBBLE_SLIDER_OPTIONS,
  DIMENSION_TO_DATA_KEY,
  QuadrantCustomizer,
  hasCustomLayoutLimits,
  isFieldAvailableInCurrentContext,
  isBubbleChartStringField,
  isBubbleChartFieldInfo,
  getAvailableFieldsOfType,
  isLabelFilterValid,
  type AvailableReferenceModeFields,
  type BubbleChartDimensionRanges,
  type FieldInfoKey,
  type FieldNameAndLabel,
  type FieldData,
} from '@ardoq/bubble-chart';
import { createDropdownSliderOptionManager } from 'viewSettings/settingsHelper';
import { localeCompareNumericLowercase, type Locale } from '@ardoq/locale';
import {
  LabelFilterContentView,
  toLabelFilterContentViewProps,
  type GetDimensionRange,
  type FieldLabels,
} from './labelFilter';
import { getQuadrantCustomizerLabels } from 'locales/getBubbleChartLabels';

const VIEW_ID = ViewIds.BUBBLE;

export const getLayoutDropdownConfig = (
  viewSettings: BubbleChartViewSettings,
  i18nLocale: I18nLocale,
  requestOpen?: Notifier
): DropdownConfig => {
  const isCustomLayout = viewSettings.background === BubbleChartGridMode.CUSTOM;
  const showInfoIcon =
    isCustomLayout &&
    hasCustomLayoutLimits(viewSettings.customQuadrantSettings);

  return {
    id: 'showBackground',
    label: t(i18nLocale, 'tabview:bubbleChart.layout.label', 'Layout'),
    iconName: IconName.LAYOUT_SETTINGS,
    options: getGridModeDropdownOptions(viewSettings, i18nLocale),
    type: SettingsType.DROPDOWN,
    popoverText: showInfoIcon
      ? t(
          i18nLocale,
          'tabview:bubbleChart.customLayoutLimitsInfo',
          'This layout has a minimum and maximum value for the X and Y axis. This may limit the bubbles that show in the view.'
        )
      : undefined,
    requestOpen,
  };
};

export const showBubblesWithZeroValueConfig = (
  {
    showBubblesWithZeroValue,
    selectedFieldNameRadius,
  }: BubbleChartViewSettings,
  i18nLocale: I18nLocale
) => {
  const isActive =
    showBubblesWithZeroValue === undefined || showBubblesWithZeroValue; // an undefined value indicates the user has not set this property yet, so we default it to true.

  const isDisabled = !selectedFieldNameRadius;
  return {
    id: 'showBubblesWithZeroValue',
    label: t(
      i18nLocale,
      'tabview:bubbleChart.showSizeZeroBubbles',
      'Show zero-size bubbles'
    ),
    type: SettingsType.TOGGLE,
    iconName: IconName.NOT_INTERESTED,
    isActive,
    onViewSettingsUpdate,
    isDisabled,
  } satisfies ToggleConfig;
};

const getSliderOption = createDropdownSliderOptionManager(
  VIEW_ID,
  new Map(),
  onViewSettingsUpdate
);

const showCustomQuadrantSettingsModal = async (
  viewSettings: BubbleChartViewSettings,
  i18nLocale: I18nLocale
) => {
  const result = await modal(resolve => (
    <QuadrantCustomizer
      close={resolve}
      initialValue={viewSettings.customQuadrantSettings}
      labels={getQuadrantCustomizerLabels(i18nLocale)}
    />
  ));
  if (!result) {
    return;
  }
  const settings: Pick<BubbleChartViewSettings, 'customQuadrantSettings'> = {
    customQuadrantSettings: result as BubbleChartQuadrantSettings,
  };
  dispatchAction(
    updateViewSettings({ viewId: VIEW_ID, settings, persistent: true })
  );
};

const getGridModeDropdownOptions = (
  viewSettings: BubbleChartViewSettings,
  i18nLocale: I18nLocale
) => {
  const result = getFieldDropdownOptions({
    viewId: VIEW_ID,
    list: [
      {
        name: BubbleChartGridMode.NONE,
        label: t(i18nLocale, 'tabview:bubbleChart.layout.none', 'None'),
      },
      {
        name: BubbleChartGridMode.GRID,
        label: t(i18nLocale, 'tabview:bubbleChart.layout.grid', 'Grid'),
      },
      {
        name: BubbleChartGridMode.TIME,
        label: t(i18nLocale, 'tabview:bubbleChart.layout.TIME', 'TIME'),
      },
      {
        name: BubbleChartGridMode.CUSTOM,
        label: t(i18nLocale, 'tabview:bubbleChart.layout.custom', 'Custom'),
        onClick: () => {
          dispatchAction(
            updateViewSettings({
              viewId: VIEW_ID,
              settings: { background: BubbleChartGridMode.CUSTOM },
              persistent: true,
            })
          );
          showCustomQuadrantSettingsModal(viewSettings, i18nLocale);
        },
      },
    ],
    settingsKey: 'background',
    isActive: name =>
      viewSettings.background === name ||
      (name === BubbleChartGridMode.NONE && !viewSettings.background),
    onViewSettingsUpdate,
  });
  return result;
};

export const getBubbleSizeScaleSliderOption = (
  viewSettings: BubbleChartViewSettings,
  i18nLocale: I18nLocale
) => {
  return getSliderOption({
    name: 'bubbleAreaScaleFactor',
    label: t(i18nLocale, 'tabview:bubbleChart.bubbleSize', 'Bubble size'),
    value: viewSettings.bubbleAreaScaleFactor,
    ...BUBBLE_SLIDER_OPTIONS,
    type: DropdownOptionType.SLIDER,
    hideValue: true,
  });
};

export const getBubbleSizeScaleSliderControl = (
  bubbleAreaScaleFactor: number,
  i18nLocale: I18nLocale
): DropdownConfig => {
  return {
    id: 'bubbleAreaScaleFactor',
    label: t(
      i18nLocale,
      'tabview:bubbleChart.changeBubbleSize',
      'Change bubble size'
    ),
    iconName: IconName.RADIO_UNSELECTED,
    options: [
      getSliderOption({
        name: 'bubbleAreaScaleFactor',
        label: t(i18nLocale, 'tabview:bubbleChart.bubbleSize', 'Bubble size'),
        value: bubbleAreaScaleFactor,
        ...BUBBLE_SLIDER_OPTIONS,
        type: DropdownOptionType.SLIDER,
        hideValue: true,
      }),
    ],
    type: SettingsType.DROPDOWN,
  };
};

export const sortedFieldNameOptions = (
  locale: Locale,
  ...args: Parameters<typeof getFieldDropdownOptions>
) => {
  return getFieldDropdownOptions(...args).sort((a, b) =>
    localeCompareNumericLowercase(a.name ?? '', b.name ?? '', locale)
  );
};

const getFieldLabelForLabelFilterButton = (
  fieldKey: FieldInfoKey,
  viewSettings: BubbleChartViewSettings,
  numericFields: FieldData[],
  referenceModeFields: AvailableReferenceModeFields
): string | null => {
  const fieldValue = viewSettings[fieldKey];

  if (!fieldValue) {
    return null;
  }

  if (
    !isFieldAvailableInCurrentContext(
      fieldValue,
      numericFields,
      referenceModeFields
    )
  ) {
    return null;
  }

  if (isBubbleChartStringField(fieldValue)) {
    const field = numericFields.find(field => field.name === fieldValue);
    return field ? field.label || field.name : null;
  }

  if (isBubbleChartFieldInfo(fieldValue)) {
    const field = getAvailableFieldsOfType(
      fieldValue.type,
      referenceModeFields
    ).find(field => field.name === fieldValue.fieldName);
    return field ? field.label || field.name : null;
  }

  return null;
};

type GetLabelFilterButtonArgs = {
  dimensionRanges: BubbleChartDimensionRanges;
  viewSettings: BubbleChartViewSettings;
  availableBubbleComponentFields: FieldNameAndLabel[];
  availableReferenceFields: FieldNameAndLabel[];
  availableReferencedComponentFields: FieldNameAndLabel[];
  numericFields: FieldData[];
  isDisabled?: boolean;
  i18nLocale: I18nLocale;
};

const getLabelFilterIconName = (
  viewSettings: BubbleChartViewSettings,
  getDimensionRange: GetDimensionRange,
  fieldLabels: FieldLabels
): IconName => {
  const labelFilter = viewSettings.labelFilter;
  if (
    !labelFilter ||
    fieldLabels[DIMENSION_TO_DATA_KEY[labelFilter.dimension]] === null ||
    !isLabelFilterValid(labelFilter, viewSettings)
  ) {
    return IconName.TAG;
  }

  const { dimension, min: filterMin, max: filterMax } = labelFilter;
  const { min: rangeMin, max: rangeMax } = getDimensionRange(dimension);

  return filterMin > rangeMin || filterMax < rangeMax
    ? IconName.TAG_OFF
    : IconName.TAG;
};

export const getLabelFilterButton = ({
  dimensionRanges,
  viewSettings,
  availableBubbleComponentFields,
  availableReferenceFields,
  availableReferencedComponentFields,
  numericFields,
  isDisabled = false,
  i18nLocale,
}: GetLabelFilterButtonArgs): DropdownConfig => {
  const getDimensionRange: GetDimensionRange = dimension =>
    dimensionRanges[dimension];
  const referenceModeFields: AvailableReferenceModeFields = {
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
  };

  const fieldLabels = {
    x: getFieldLabelForLabelFilterButton(
      'selectedFieldNameX',
      viewSettings,
      numericFields,
      referenceModeFields
    ),
    y: getFieldLabelForLabelFilterButton(
      'selectedFieldNameY',
      viewSettings,
      numericFields,
      referenceModeFields
    ),
    radius: getFieldLabelForLabelFilterButton(
      'selectedFieldNameRadius',
      viewSettings,
      numericFields,
      referenceModeFields
    ),
  };

  return {
    id: 'labelFilter',
    label: t(
      i18nLocale,
      'tabview:bubbleChart.declutterLabels',
      'Declutter labels'
    ),
    iconName: getLabelFilterIconName(
      viewSettings,
      getDimensionRange,
      fieldLabels
    ),
    type: SettingsType.DROPDOWN,
    isDisabled,
    isKeepOpen: true,
    options: [
      {
        name: 'labelFilter-content',
        type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
        content: (
          <LabelFilterContentView
            key="labelFilter-content"
            i18nLocale={i18nLocale}
            {...toLabelFilterContentViewProps({
              getDimensionRange,
              viewSettings,
              fieldLabels,
              i18nLocale,
            })}
          />
        ),
      },
    ],
  };
};
